/** @format  */

import type { PdfBlock, PdfQuestion } from "./types";

/**
 * The single source of truth for both renderers:
 * the react-pdf export  (`pdf/`) and the on-screen -HTML twin (`preview/`). Change a value here and
 * the exported PDF and the PDF view page move together
 *
 * TODO: hardcoded neutral colours - wire to brand colour palette.
 *
 */

export const DEFAULT_MARKS = 1;
export const LINES_PER_MARK = 2;

export const NUMBER_WIDTH = 40;
export const LABEL_WIDTH = 44;

export const ANSWER_LINES_INDENT = NUMBER_WIDTH + LABEL_WIDTH;

/** Indentation per depth level for hierarchical question parts (in points). */
export const HIERARCHY_INDENT_PER_LEVEL = 20;

export const MARKS_WIDTH = 30;
export const SVG_DISPLAY_WIDTH = 576;
export const NUMBER_FONT_SIZE = 11;
export const BODY_FONT_SIZE = 10;
export const FOOTNOTE_FONT_SIZE = 9;
export const TEXT_PADDING_RIGHT = 10;
export const PART_MARGIN_BOTTOM = 26;
export const PART_MARGIN_BOTTOM_LAST = 12;
export const ANSWER_LINE_HEIGHT = 20;
export const ANSWER_LINES_MARGIN_TOP = 10;
export const ANSWER_LINES_MARGIN_RIGHT = 16;
export const BLOCK_GAP = 8; // spacing between content blocks
export const IMAGE_BLOCK_GAP = 12;
export const SOURCE_MARGIN_TOP = 22;
export const SOURCE_MARGIN_BOTTOM = 24;

//Title page: center logo and title block
export const LOGO_WIDTH = 160;
export const LOGO_MARGIN_BOTTOM = 40;
export const TITLE_FONT_SIZE = 20;
export const TITLE_MARGIN_BOTTOM = 30;

//marks table
export const MARKS_TABLE_WIDTH = 300;
export const MARKS_TABLE_BORDER_WIDTH = 1;
export const MARKS_TABLE_CELL_PADDING = 6;
export const MARKS_TABLE_MARGIN_TOP = 20;
export const MARKS_TABLE_MARGIN_BOTTOM = 50;

// section pages (examiner / marking scheme) reuse the questions pages geomtry and type scale
// only the heading and the label column are their own

export const SECTION_TITLE_FONT_SIZE = 14;
export const SECTION_TITLE_MARGIN_BOTTOM = 18;

// Gap between a section heading and the subtle note beneath it (the examiner comments note)
export const SECTION_NOTE_MARGIN_TOP = 6;

export const SECTION_LABEL_WIDTH = NUMBER_WIDTH + LABEL_WIDTH;

// shared by both renderers
export const BODY_LINE_HEIGHT = 1.4;
// Physical page geometry
export const PAGE_PADDING_TOP = "1.8cm";
export const PAGE_PADDING_BOTTOM = "1.6cm";
export const PAGE_PADDING_HORIZONTAL = "1.8cm";
export const A4_WIDTH = "21cm";
export const A4_MIN_HEIGHT = "29.7cm";

export const COLOR_TEXT = "#1a1a1a";
export const COLOR_TABLE_BORDER = "#000000";
export const COLOR_LABEL = "#333333";
export const COLOR_MISSING = "#555555";
export const COLOR_SOURCE = "#888888";
export const COLOR_ANSWER_LINE = "#bbbbbb";

// Render a point metric as a CSS length for the HTML twin, react-pdf takes the raw number
export const pt = (value: number): string => `${value}pt`;

// Ruled answer lines for a part: two per mark.
export const answerLineCount = (maxMarks: number | null): number => (maxMarks ?? DEFAULT_MARKS) * LINES_PER_MARK;

export const blockGapAbove = (previous: PdfBlock, current: PdfBlock): number =>
	previous.kind === "image" || current.kind === "image" ? IMAGE_BLOCK_GAP : BLOCK_GAP;

// Assembled paper position shown on a question's first part (e.g. "1").
export const formatPaperNumber = (groupIndex: number): string => `${groupIndex + 1}.`;

/**
 * Part label for the questions page label column. Now that sub-parts are
 * indented, only the leaf suffix is needed — the parent context is conveyed
 * by the indentation and the parts above.
 *
 * Examples:
 *   - `"1"`       → `""`  (root, shown as the bold paper number)
 *   - `"1(a)"`    → `"(a)"`
 *   - `"1(d)(i)"` → `"(i)"`
 */

export const partLabel = (displayLabel: string, precedingLabels: string[] = []): string => {
	const parentLabel = precedingLabels
		.filter((label) => label !== displayLabel && displayLabel.startsWith(label))
		.reduce((longest, label) => (label.length > longest.length ? label : longest), "");
	return parentLabel === "" ? displayLabel.replace(/^\d+/, "") : displayLabel.slice(parentLabel.length);
};

// Marks at the end of a part's text line (e.g. "[2]").
export const formatMarks = (maxMarks: number): string => `[${maxMarks}]`;

// split the flat list into contiguous runs sharing a groupId (one root question + its parts).
export const groupByQuestion = (questions: PdfQuestion[]): PdfQuestion[][] => {
	const groups: PdfQuestion[][] = [];
	for (const question of questions) {
		const current = groups[groups.length - 1];
		if (current && current[0].groupId === question.groupId) {
			current.push(question);
		} else {
			groups.push([question]);
		}
	}
	return groups;
};

export const isMergedHeader = (group: PdfQuestion[], index: number): boolean => {
	const header = group[index];
	const next = group[index + 1];
	if (header === undefined || next === undefined) {
		return false;
	}
	return (
		header.isParentHeader &&
		!next.isParentHeader &&
		next.displayLabel.startsWith(header.displayLabel) &&
		next.displayLabel.length > header.displayLabel.length
	);
};
